import { createSlice } from '@reduxjs/toolkit';
import { API } from 'api';

const initialState = {
	paymentQueue: { rows: [], count: 0 },
	paymentHistory: { rows: [], count: 0 },
	loading: {
		paymentQueue: false,
		paymentHistory: false,
	},
	error: {
		paymentQueue: null,
		paymentHistory: null,
	},
};

export const financeSliceV2 = createSlice({
	name: 'financeV2',
	initialState,
	reducers: {
		setPaymentQueueV2: (state, action) => {
			state.paymentQueue = {
				rows: action.payload.rows || [],
				count: action.payload.count || 0,
			};
			state.loading.paymentQueue = false;
			state.error.paymentQueue = null;
		},
		setPaymentHistoryV2: (state, action) => {
			state.paymentHistory = {
				rows: action.payload.rows || [],
				count: action.payload.count || 0,
			};
			state.loading.paymentHistory = false;
			state.error.paymentHistory = null;
		},
		setLoadingV2: (state, action) => {
			const { tab, loading } = action.payload;
			state.loading[tab] = loading;
		},
		setErrorV2: (state, action) => {
			const { tab, error } = action.payload;
			state.error[tab] = error;
			state.loading[tab] = false;
		},
	},
});

export const { setPaymentQueueV2, setPaymentHistoryV2, setLoadingV2, setErrorV2 } =
	financeSliceV2.actions;

// Helper function to build filterBy object
const buildFilterBy = ({
	query_str,
	city,
	client_id,
	project_id,
	vendor_id,
	business_unit_ids,
	from_date,
	to_date,
	...rest
}) => {
	const filterBy = {};

	if (query_str) filterBy.query_str = query_str;
	if (city) filterBy.city = city;
	if (client_id) filterBy.client_id = client_id;
	if (project_id) filterBy.project_id = project_id;
	if (vendor_id) filterBy.vendor_id = vendor_id;
	if (business_unit_ids && business_unit_ids.length > 0)
		filterBy.business_unit_ids = business_unit_ids;
	if (from_date) filterBy.created_from = from_date;
	if (to_date) filterBy.created_to = to_date;

	return { ...filterBy, ...rest };
};

/**
 * Helper to convert limit/offset to page/per_page
 * Components pass limit+offset, contract expects page+per_page
 */
const toPagination = (limit = 10, offset = 0) => ({
	page: Math.floor(offset / limit) + 1,
	per_page: limit,
});

/**
 * Helper to parse V2 search response
 * Contract response: { data: { finance_requests: [...], pagination: { total } } }
 * Components expect: { rows: [...], count: N }
 */
const parseSearchResponse = (response) => {
	// Try multiple possible response structures
	// V2 API returns: { data: [...], total_count: N } or { data: { finance_requests: [...], pagination: { total } } }
	const rows =
		// Check if data is an array directly (V2 format)
		(Array.isArray(response?.data) && response.data) ||
		response?.data?.finance_requests ||
		response?.data?.data?.finance_requests ||
		response?.data?.rows ||
		response?.data?.data ||
		response?.data ||
		[];

	const count =
		// V2 API returns total_count at root level
		response?.total_count ||
		response?.data?.pagination?.total ||
		response?.data?.data?.pagination?.total ||
		response?.data?.count ||
		response?.data?.data?.count ||
		response?.data?.total ||
		response?.data?.data?.total ||
		response?.meta?.count ||
		response?.meta?.total ||
		response?.count ||
		0;

	return { rows, count };
};

/**
 * Fetch Payment Queue V2
 * POST /api/v2/search
 * Contract: { search_type: 'finance_request', filter_by: {...}, pagination: { page, per_page } }
 */
export const fetchPaymentQueueV2 =
	({
		limit = 10,
		offset = 0,
		query_str,
		city,
		client_id,
		project_id,
		vendor_id,
		business_unit_ids,
		from_date,
		to_date,
		...rest
	}) =>
	async (dispatch) => {
		dispatch(setLoadingV2({ tab: 'paymentQueue', loading: true }));
		try {
			const filterBy = buildFilterBy({
				query_str,
				city,
				client_id,
				project_id,
				vendor_id,
				business_unit_ids,
				from_date,
				to_date,
				...rest,
			});

			const CREDENTIALS = {
				url: '/search',
				data: {
					search_type: 'finance_request',
					offset,
					limit,
					filter_by: {
						tab: 'payments_queue',
						...filterBy,
					},
				},
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);
			console.log('📥 [fetchPaymentQueueV2] API response:', response);
			const result = parseSearchResponse(response);
			console.log('📊 [fetchPaymentQueueV2] Parsed result:', result);
			dispatch(setPaymentQueueV2(result));
			return result;
		} catch (error) {
			dispatch(
				setErrorV2({
					tab: 'paymentQueue',
					error: error.message || 'Failed to fetch payment queue',
				})
			);
			throw error;
		}
	};

/**
 * Fetch Payment History V2
 * POST /api/v2/search
 * Contract: { search_type: 'finance_request', filter_by: {...}, pagination: { page, per_page } }
 */
export const fetchPaymentHistoryV2 =
	({
		limit = 10,
		offset = 0,
		query_str,
		city,
		client_id,
		project_id,
		vendor_id,
		business_unit_ids,
		from_date,
		to_date,
		...rest
	}) =>
	async (dispatch) => {
		dispatch(setLoadingV2({ tab: 'paymentHistory', loading: true }));
		try {
			const filterBy = buildFilterBy({
				query_str,
				city,
				client_id,
				project_id,
				vendor_id,
				business_unit_ids,
				from_date,
				to_date,
				...rest,
			});

			const CREDENTIALS = {
				url: '/search',
				data: {
					search_type: 'finance_request',
					offset,
					limit,
					filter_by: {
						tab: 'payments_history',
						...filterBy,
					},
				},
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);
			const result = parseSearchResponse(response);
			dispatch(setPaymentHistoryV2(result));
			return result;
		} catch (error) {
			dispatch(
				setErrorV2({
					tab: 'paymentHistory',
					error: error.message || 'Failed to fetch payment history',
				})
			);
			throw error;
		}
	};

/**
 * Fetch Finance Request Queue V2 (Requests page)
 * POST /api/v2/search
 * Contract: { search_type: 'finance_request', filter_by: { tab: 'request_queue', ... }, pagination: {...} }
 */
export const fetchRequestQueueV2 =
	({
		limit = 10,
		offset = 0,
		query_str,
		city,
		client_id,
		project_id,
		vendor_id,
		business_unit_ids,
		from_date,
		to_date,
		...rest
	}) =>
	async () => {
		const filterBy = buildFilterBy({
			query_str,
			city,
			client_id,
			project_id,
			vendor_id,
			business_unit_ids,
			from_date,
			to_date,
			...rest,
		});

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'finance_request',
				filter_by: {
					tab: 'requests_queue',
					...filterBy,
				},
				pagination: toPagination(limit, offset),
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return parseSearchResponse(response);
	};

/**
 * Fetch Finance Request History V2 (Requests page)
 * POST /api/v2/search
 * Contract: { search_type: 'finance_request', filter_by: { tab: 'request_history', ... }, pagination: {...} }
 */
export const fetchRequestHistoryV2 =
	({
		limit = 10,
		offset = 0,
		query_str,
		city,
		client_id,
		project_id,
		vendor_id,
		business_unit_ids,
		from_date,
		to_date,
		unmarked_only,
		...rest
	}) =>
	async () => {
		const filterBy = buildFilterBy({
			query_str,
			city,
			client_id,
			project_id,
			vendor_id,
			business_unit_ids,
			from_date,
			to_date,
			...rest,
		});

		if (unmarked_only) filterBy.unmarked_only = true;

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'finance_request',
				filter_by: {
					tab: 'request_history',
					...filterBy,
				},
				pagination: toPagination(limit, offset),
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return parseSearchResponse(response);
	};

/**
 * Fetch Finance Request Status History V2
 * POST /api/v2/search
 * Contract: { search_type: 'finance_request_status_history', filter_by: { finance_request_id } }
 */
export const fetchFinanceRequestStatusHistoryV2 =
	({ finance_request_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'finance_request_status_history',
				filter_by: {
					finance_request_id,
				},
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		// API returns { data: [...], status: "success", total_count: N }
		// The data array contains the status history items directly
		const statusHistory = Array.isArray(response?.data) ? response.data : [];

		return {
			finance_request_id,
			status_history: statusHistory,
		};
	};

/**
 * Fetch Finance Request Navigation V2
 * POST /api/v2/search
 * Contract: { search_type: 'finance_request_navigation', filter_by: { current_request_id } }
 */
export const fetchFinanceRequestNavigationV2 =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'finance_request_navigation',
				filter_by: {
					current_request_id: id,
				},
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return response?.data || null;
	};

/**
 * Fetch Finance Request Detail V2
 * POST /api/v2/search
 */
export const fetchFinanceRequestDetailV2 =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'finance_request',
				filter_by: { id },
				pagination: { page: 1, per_page: 1 },
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return parseSearchResponse(response);
	};

/**
 * Generate Proforma Invoice V2
 * POST /api/v2/update  { update_type: 'generate_proforma_invoice', data: { id: finance_request_id } }
 */
export const generateProformaInvoiceV2 =
	({ finance_request_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: {
				update_type: 'generate_proforma_invoice',
				data: {
					id: finance_request_id,
				},
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return {
			pdf_url: response?.data?.pdf_url || null,
			proforma_invoice_number: response?.data?.proforma_invoice_number || null,
			is_supervisor_uploaded: response?.data?.is_supervisor_uploaded || false,
			document_url: response?.data?.pdf_url || null,
			download_url: response?.data?.pdf_url || null,
			invoice_number: response?.data?.proforma_invoice_number || null,
		};
	};

/**
 * Fetch Proforma Invoice V2 (View/Download)
 * POST /api/v2/search  { search_type: 'proforma_invoice_pdf', data: { id, url_type } }
 */
export const fetchProformaInvoiceV2 =
	({ finance_request_id, url_type = 'view' }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'proforma_invoice_pdf',
				data: {
					id: finance_request_id,
					url_type,
				},
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return {
			pdf_url: response?.data?.pdf_url || null,
			proforma_invoice_number: response?.data?.proforma_invoice_number || null,
			is_supervisor_uploaded: response?.data?.is_supervisor_uploaded || false,
			view_url: response?.data?.view_url || response?.data?.signed_url || null,
			document_url:
				response?.data?.view_url ||
				response?.data?.signed_url ||
				response?.data?.pdf_url ||
				response?.data?.download_url ||
				null,
			download_url: response?.data?.download_url || response?.data?.pdf_url || null,
			invoice_data: response?.data || null,
		};
	};

/**
 * Download Proforma Invoice V2
 * POST /api/v2/search  { search_type: 'proforma_invoice_pdf', data: { id, url_type: 'download' } }
 */
export const downloadProformaInvoiceV2 =
	({ finance_request_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'proforma_invoice_pdf',
				data: {
					id: finance_request_id,
					url_type: 'download',
				},
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return {
			download_url: response?.data?.download_url || null,
		};
	};

/**
 * Approve Payment V2 (Record Payment)
 * POST /api/v2/update
 * Contract: { update_type: 'finance_request', data: { id, status: 'Paid', payment_info: { payment_date, utr_number, ... } } }
 */
export const approvePaymentV2 =
	({ finance_request_id, paid_date, utr_number, paid_amount, login_user_id }) =>
	async (dispatch, getState) => {
		const state = getState();
		const userId = login_user_id || state?.auth?.me?.login_user_id || null;

		const CREDENTIALS = {
			url: '/update',
			data: {
				update_type: 'finance_request',
				data: {
					id: finance_request_id,
					status: 'Marked',
					utr_number,
					paid_date,
					paid_amount,
					payment_status: 'Completed',
					...(userId && { login_user_id: userId }),
				},
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return response?.data || null;
	};

/**
 * Reject Payment V2
 * POST /api/v2/update
 * Contract: { update_type: 'finance_request', data: { id, status: 'Rejected' } }
 */
export const rejectPaymentV2 =
	({ finance_request_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: {
				update_type: 'finance_request',
				data: {
					id: finance_request_id,
					status: 'Unmarked',
				},
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return response?.data || null;
	};

/**
 * Mark for Payment V2 (Approve)
 * POST /api/v2/update
 * Contract: { update_type: 'finance_request', data: { id, status: 'Approved' } }
 */
export const markForPaymentV2 =
	({ finance_request_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: {
				update_type: 'finance_request',
				data: {
					id: finance_request_id,
					status: 'Marked',
				},
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return response?.data || null;
	};

/**
 * Bulk Mark for Payment V2
 * POST /api/v2/update
 * Contract: { update_type: 'finance_request', data: { ids: [...], status: 'Approved' } }
 */
export const bulkMarkForPaymentV2 =
	({ ids }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: {
				update_type: 'finance_request',
				data: {
					ids,
					status: 'Marked',
				},
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return response?.data || null;
	};

/**
 * Bulk Unmark V2
 * POST /api/v2/update
 * Contract: { update_type: 'finance_request', data: { ids: [...], status: 'Rejected' } }
 */
export const bulkUnmarkPaymentV2 =
	({ ids }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: {
				update_type: 'finance_request',
				data: {
					ids,
					status: 'Rejected',
				},
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return response?.data || null;
	};

// Helper function to upload file to S3
const uploadFileToS3 = async (url, file, contentType) => {
	const response = await fetch(url, {
		method: 'PUT',
		headers: { 'Content-Type': contentType },
		body: file,
	});

	if (!response.ok) {
		throw new Error(`S3 upload failed: HTTP ${response.status}`);
	}

	return { success: true };
};

/**
 * Upload Proforma Invoice V2
 * Uses get-signed-url + S3 upload + update via V2
 * Optional invoiceNumber and invoiceDate ('YYYY-MM-DD') are the details printed on the file.
 */
export const uploadProformaInvoiceV2 =
	({ file, id, invoiceNumber, invoiceDate }) =>
	async () => {
		// Step 1: Get signed URL (uses v1 API endpoint)
		const signedUrlCredentials = {
			url: '/get-signed-url',
			data: {
				url_type: 'upload',
				data: {
					entity: 'trip',
					keys: [file.name],
				},
			},
			// Use v1 API endpoint (no version specified defaults to v1)
		};

		const signedUrlResponse = await API.common(signedUrlCredentials);
		const fileData = signedUrlResponse?.data?.[0];

		if (!fileData?.url || !fileData?.filename) {
			throw new Error('Failed to get signed URL from server');
		}

		// Step 2: Upload to S3
		await uploadFileToS3(fileData.url, file, file.type);

		// Step 3: Update the finance request with the uploaded file key
		const updateCredentials = {
			url: '/update',
			data: {
				update_type: 'upload_proforma_invoice',
				data: {
					id,
					key: fileData.filename,
					// Number/date printed on the uploaded invoice; both optional (blank -> null)
					invoice_number: invoiceNumber ? invoiceNumber.trim() : null,
					invoice_date: invoiceDate || null,
				},
			},
			version: 'v2',
		};

		const response = await API.common(updateCredentials);
		return response?.data || response || null;
	};

/**
 * Create Finance Request from Adhoc V2
 * POST /api/v2/create
 * Contract: { create_type: 'finance_request', data: { adhoc_request_id, request_type, amount, payment_details, request_info } }
 */
export const createFinanceRequestV2 =
	({ adhoc_request_id, request_type, amount, payment_details, request_info }) =>
	async () => {
		const CREDENTIALS = {
			url: '/create',
			data: {
				create_type: 'finance_request',
				data: {
					adhoc_request_id,
					request_type,
					amount,
					...(payment_details && { payment_details }),
					...(request_info && { request_info }),
				},
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return response?.data || null;
	};

/**
 * Bulk Update UTR V2
 * POST /api/v2/bulk_upload
 * Contract: { upload_type: 'finance_utr', file: '<base64>', options: { validate_utr, skip_invalid } }
 */
export const bulkUpdateUTRV2 =
	({ file, options = { validate_utr: true, skip_invalid: false } }) =>
	async () => {
		// Convert file to base64
		const toBase64 = (f) =>
			new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.readAsDataURL(f);
				reader.onload = () => resolve(reader.result.split(',')[1]);
				reader.onerror = (error) => reject(error);
			});

		const base64File = await toBase64(file);

		const CREDENTIALS = {
			url: '/bulk_upload',
			data: {
				upload_type: 'finance_utr',
				file: base64File,
				options,
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return response?.data || null;
	};

/**
 * Export Finance Payment CSV V2
 * POST /api/v2/generate-report  { report_type: 'finance_request_export', file_type: 'csv', data: { filter_by: {...} } }
 */
export const exportFinancePaymentCSVV2 =
	({ filter_by = {} }) =>
	async () => {
		const CREDENTIALS = {
			url: '/generate-report',
			data: {
				report_type: 'finance_request_export',
				file_type: 'csv',
				data: {
					filter_by,
				},
			},
			version: 'v2',
			aclBlockApiCall: false,
		};

		const response = await API.common(CREDENTIALS);
		if (response?.data?.download_url) {
			window.open(response.data.download_url);
		}
		return response;
	};

/**
 * Search Employee V2
 * POST /api/v2/search  { search_type: 'employee', filter_by: { query_str, limit, offset } }
 */
export const searchEmployeeV2 =
	({ query_str, limit = 1, offset = 0 }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'employee',
				filter_by: {
					query_str,
					limit,
					offset,
				},
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return {
			data: response?.data || [],
			count: response?.meta?.count || 0,
		};
	};

/**
 * Get Filter List V2 (client/project/vendor)
 * POST /api/v2/list_search  { list_search_type: '<type>', filter_by: {}, limit: 100 }
 */
export const getFilterListV2 =
	({ filter }) =>
	async () => {
		const CREDENTIALS = {
			url: '/list_search',
			data: {
				list_search_type: filter,
				filter_by: {},
				limit: 1000,
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return {
			rows: response?.data?.results || response?.data || [],
			count: response?.data?.total || response?.meta?.count || 0,
		};
	};

/**
 * Get Employee Filters V2 (cities, clients, projects)
 * POST /api/v2/list_search  { list_search_type: 'employee', filter_list: ['cities', 'clients', 'projects'] }
 */
export const getEmployeeFiltersV2 = () => async () => {
	const CREDENTIALS = {
		url: '/list_search',
		data: {
			list_search_type: 'employee',
			filter_list: ['cities', 'clients', 'projects'],
		},
		version: 'v2',
	};

	const response = await API.common(CREDENTIALS);
	return {
		cities: response?.data?.cities || [],
		clients: response?.data?.clients || [],
		projects: response?.data?.projects || [],
	};
};

/**
 * Get Business Units V2
 * POST /api/v2/search  { search_type: 'business_unit', filter_by: {}, pagination: { page, per_page } }
 */
export const getBusinessUnitsV2 =
	({ limit = 100, offset = 0 } = {}) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'business_unit',
				filter_by: {},
				pagination: toPagination(limit, offset),
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return {
			rows: response?.data || [],
			count: response?.meta?.count || 0,
		};
	};

/**
 * Get Signed URL V2 (uses v1 API endpoint)
 * POST /api/v1/get-signed-url  { url_type, data: { entity, keys } }
 * Note: This function is named V2 for consistency but uses v1 API endpoint
 */
export const getSignedUrlV2 = (data) => async () => {
	const CREDENTIALS = {
		url: '/get-signed-url',
		data,
		// Use v1 API endpoint (no version specified defaults to v1)
	};

	return API.common(CREDENTIALS).then(async (response) => response);
};

export default financeSliceV2.reducer;
